/* ==========================================================================
   LOYAL · T3 · THE MOTION RUNTIME
   Owner T3. Deferred, no dependencies, no library, no framework.
   Partner file to loyal-motion.css. Read that file's header first: it explains
   the property ownership split that this file is the other half of.

   WHAT THIS FILE IS ALLOWED TO DO, and it is a short list on purpose.
   It writes FIVE custom properties and NOTHING else:
     --loyal-motion-magnet-x     --loyal-motion-magnet-y
     --loyal-motion-pointer-x    --loyal-motion-pointer-y
     --loyal-motion-velocity     --loyal-motion-progress
   It never sets a class, never sets style.transform, never touches an element
   loyal-home.js owns, and never reveals or hides anything. The reveal system
   stays in loyal-home.js with exactly one owner, because two scripts racing to
   decide whether an element is visible is how the hero CTA bug happened.

   WHY IT IS SEPARATE FROM loyal-home.js.
   loyal-home.js is REQUIRED behaviour: the countdown, the jump, the reveal
   floor. This is OPTIONAL polish. Splitting them means this file can be
   deleted, blocked by an extension, or fail to parse on an old engine and the
   page still counts down, still reveals and still converts. Merging them would
   put a cursor effect in the same failure domain as the countdown.

   THE PERFORMANCE CONTRACT, because the board has a 60fps veto.
   1  ONE rAF LOOP for the whole page, and it does not run when there is
      nothing to do. It stops itself the moment velocity has decayed and no
      parallax element is on screen.
   2  LAYOUT IS READ ON RESIZE, NEVER PER FRAME. Element geometry is measured
      once and cached. Per frame the loop reads window.scrollY, which is not a
      layout read, and writes custom properties, which is not a layout write.
   3  PARALLAX IS FALLBACK ONLY. If the browser has native scroll driven
      animation, this file does not run parallax at all and the compositor owns
      it entirely. The JS path exists so Firefox and older Safari get the idea
      rather than a flat page, and it is the only effect given a fallback.
   4  EVERY LISTENER IS PASSIVE. Nothing here can block a scroll.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // DEFAULT DENY. Under reduced motion this file does not merely animate less,
  // it does not attach a single listener. The CSS floors already guarantee the
  // static page is correct, so there is nothing left for the runtime to do.
  if (REDUCED) return;

  var FINE = window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var supports = (window.CSS && CSS.supports) ? CSS.supports.bind(CSS) : null;
  var HAS_VIEW_TIMELINE = !!supports && supports('animation-timeline: view()');
  var HAS_SCROLL_TIMELINE = !!supports && supports('animation-timeline: scroll()');

  var root = document.documentElement;
  var raf = window.requestAnimationFrame || function (f) { return setTimeout(f, 16); };

  /* ------------------------------------------------------------------------
     THE FRAME LOOP
     Started on demand, stopped as soon as every subscriber reports it has
     nothing left to move. A loop that runs forever on an idle page is a
     battery cost with no pixel to show for it.
     ---------------------------------------------------------------------- */
  var subscribers = [];
  var running = false;

  function frame() {
    var wants = false;
    for (var i = 0; i < subscribers.length; i++) {
      // A subscriber returns true while it still has work. Any true keeps the
      // loop alive for everyone, which is correct: one loop, not three.
      if (subscribers[i]()) wants = true;
    }
    if (wants) {
      raf(frame);
    } else {
      running = false;
    }
  }

  function kick() {
    if (running) return;
    running = true;
    raf(frame);
  }

  /* ------------------------------------------------------------------------
     1 · SCROLL VELOCITY

     Feeds --loyal-motion-velocity, which the marquee window leans by. Clamped
     to the range minus one to one and decayed toward zero, so a flick produces
     a lean that settles rather than a value that sticks at the last frame's
     number and leaves the marquee permanently crooked.

     THE CLAMP IS NOT COSMETIC. An unclamped delta from a trackpad flick or a
     programmatic jump can be thousands of pixels in one frame, which would
     skew the marquee past legibility and, on the jump case, do it in a single
     frame with no motion to explain it.
     ---------------------------------------------------------------------- */
  function velocity() {
    var el = document.querySelector('[data-loyal-velocity]');
    if (!el) return;

    var MAX_DELTA = 90;     // px per frame that maps to full lean
    var DECAY = 0.86;       // per frame. Settles visually in about 15 frames.
    var EPSILON = 0.004;    // below this it is zero and the loop may stop

    var last = window.scrollY || window.pageYOffset || 0;
    var value = 0;
    var pending = 0;

    subscribers.push(function () {
      var now = window.scrollY || window.pageYOffset || 0;
      var delta = now - last;
      last = now;

      pending = delta / MAX_DELTA;
      if (pending > 1) pending = 1;
      if (pending < -1) pending = -1;

      // Take the larger of the fresh impulse and the decaying tail, so a
      // sustained scroll holds the lean instead of fighting its own decay.
      value = Math.abs(pending) > Math.abs(value) ? pending : value * DECAY;

      if (Math.abs(value) < EPSILON) {
        value = 0;
        root.style.setProperty('--loyal-motion-velocity', '0');
        return false;             // nothing left to move
      }
      root.style.setProperty('--loyal-motion-velocity', value.toFixed(4));
      return true;
    });

    window.addEventListener('scroll', kick, { passive: true });
  }

  /* ------------------------------------------------------------------------
     2 · PARALLAX FALLBACK

     ONLY RUNS WHERE THE BROWSER HAS NO NATIVE VIEW TIMELINE. Where it does,
     loyal-motion.css owns parallax on the compositor and this function returns
     immediately, which is the whole reason the native path is preferred.

     It writes a transform through a custom property rather than through
     style.transform, so an element can carry a parallax offset AND whatever
     transform its own stylesheet gives it without either clobbering the other.
     ---------------------------------------------------------------------- */
  function parallax() {
    if (HAS_VIEW_TIMELINE) return;

    var nodes = Array.prototype.slice.call(
      document.querySelectorAll('[data-loyal-parallax]'));
    if (!nodes.length) return;

    var items = [];
    var live = 0;

    function measure() {
      // The one layout read, and it happens on resize, not per frame.
      var scrollTop = window.scrollY || window.pageYOffset || 0;
      for (var i = 0; i < items.length; i++) {
        var r = items[i].el.getBoundingClientRect();
        items[i].top = r.top + scrollTop;
        items[i].height = r.height;
      }
    }

    nodes.forEach(function (el) {
      var depthName = el.getAttribute('data-loyal-parallax') || 'mid';
      var depth = depthName === 'near' ? 40 :
        (depthName === 'far' || depthName === 'free') ? 12 : 24;
      // THE OVERSIZE MUST MATCH THE CSS PLANE FOR PLANE, or the two paths
      // disagree about how big the hero is and Firefox renders a product 14
      // percent larger than Chrome does. `free` has no frame to clip against,
      // so it does not scale, exactly as loyal-motion.css declares.
      var scale = depthName === 'free' ? 1 : 1.14;
      items.push({
        el: el, depth: depth, scale: scale, top: 0, height: 0, visible: false
      });
    });

    // Only elements actually on screen are computed. On a long homepage that
    // is two or three out of a dozen at any moment.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          for (var i = 0; i < items.length; i++) {
            if (items[i].el !== entry.target) continue;
            if (items[i].visible !== entry.isIntersecting) {
              items[i].visible = entry.isIntersecting;
              live += entry.isIntersecting ? 1 : -1;
            }
          }
        });
        if (live > 0) kick();
      }, { rootMargin: '20% 0px 20% 0px' });
      items.forEach(function (it) { io.observe(it.el); });
    } else {
      // No observer means compute everything. Correct, just less thrifty.
      items.forEach(function (it) { it.visible = true; });
      live = items.length;
    }

    subscribers.push(function () {
      if (live <= 0) return false;
      var scrollTop = window.scrollY || window.pageYOffset || 0;
      var vh = window.innerHeight || 1;

      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it.visible) continue;

        // progress runs 0 as the element's top meets the bottom of the
        // viewport, to 1 as its bottom leaves the top. Same range the native
        // `cover 0% cover 100%` describes, so both paths look identical.
        var span = vh + it.height;
        var p = (scrollTop + vh - it.top) / (span || 1);
        if (p < 0) p = 0;
        if (p > 1) p = 1;

        var offset = (p * 2 - 1) * it.depth;
        it.el.style.transform =
          'translate3d(0, ' + offset.toFixed(2) + 'px, 0) scale(' + it.scale + ')';
      }
      return true;
    });

    measure();
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', function () { measure(); kick(); }, { passive: true });
    window.addEventListener('load', function () { measure(); kick(); });
    kick();
  }

  /* ------------------------------------------------------------------------
     3 · PROGRESS RAIL FALLBACK
     Same shape as parallax: native scroll timeline owns it where it exists.
     ---------------------------------------------------------------------- */
  function progress() {
    if (HAS_SCROLL_TIMELINE) return;
    var rail = document.querySelector('[data-loyal-progress]');
    if (!rail) return;

    function update() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - doc.clientHeight;
      var p = max > 0 ? (window.scrollY || window.pageYOffset || 0) / max : 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      root.style.setProperty('--loyal-motion-progress', p.toFixed(4));
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  /* ------------------------------------------------------------------------
     4 · THE MAGNETIC CONTROL

     FINE POINTER ONLY. On touch there is no cursor to be attracted to, and a
     pointermove on a touch device fires only during a drag, which would make
     the site's primary CTA slide out from under the thumb that is pressing it.

     THE CONTROL IS ALWAYS A REAL FOCUSABLE ELEMENT and nothing here changes
     what it is. Keyboard, Enter and Space already work because this never
     intercepts them, and the attraction is a decoration on top of a button
     that behaves identically without it.
     ---------------------------------------------------------------------- */
  function magnetic(el) {
    if (!FINE) return;
    if (el.getAttribute('data-loyal-motion-bound') === 'magnet') return;
    el.setAttribute('data-loyal-motion-bound', 'magnet');

    var STRENGTH = 0.28;
    var MAX = 10;

    function clampToMax(n) {
      return n > MAX ? MAX : n < -MAX ? -MAX : n;
    }

    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      var dx = clampToMax((e.clientX - (r.left + r.width / 2)) * STRENGTH);
      var dy = clampToMax((e.clientY - (r.top + r.height / 2)) * STRENGTH);
      el.setAttribute('data-loyal-magnet', 'live');
      el.style.setProperty('--loyal-motion-magnet-x', dx.toFixed(2) + 'px');
      el.style.setProperty('--loyal-motion-magnet-y', dy.toFixed(2) + 'px');
    }, { passive: true });

    function release() {
      // The attribute comes off FIRST so the spring transition in the CSS is
      // in place before the values change. Removing it after would snap.
      el.removeAttribute('data-loyal-magnet');
      el.style.setProperty('--loyal-motion-magnet-x', '0px');
      el.style.setProperty('--loyal-motion-magnet-y', '0px');
    }

    el.addEventListener('pointerleave', release, { passive: true });
    el.addEventListener('pointercancel', release, { passive: true });
    el.addEventListener('blur', release);
  }

  /* ------------------------------------------------------------------------
     5 · THE SPOTLIGHT
     Writes where the cursor is, in percent, on the element under it. The CSS
     paints it on a pseudo element. Fine pointer only, same reason as above.
     ---------------------------------------------------------------------- */
  function spotlight(el) {
    if (!FINE) return;
    if (el.getAttribute('data-loyal-motion-bound') === 'spot') return;
    el.setAttribute('data-loyal-motion-bound', 'spot');

    el.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      el.style.setProperty(
        '--loyal-motion-pointer-x',
        (((e.clientX - r.left) / r.width) * 100).toFixed(2) + '%');
      el.style.setProperty(
        '--loyal-motion-pointer-y',
        (((e.clientY - r.top) / r.height) * 100).toFixed(2) + '%');
    }, { passive: true });
  }

  /* ------------------------------------------------------------------------
     BOOT
     The one shot work guards itself with a flag rather than by rebinding,
     because shopify:section:load fires on every section re render in the theme
     editor and a second velocity subscriber would push the marquee twice as
     far on the same scroll.
     ---------------------------------------------------------------------- */
  var once = false;

  function boot() {
    if (!once) {
      once = true;
      velocity();
      parallax();
      progress();
    }
    // These two are per element and idempotent by attribute, so a section that
    // re renders picks up its own new nodes and never rebinds an old one.
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-loyal-magnetic]'), magnetic);
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-loyal-spotlight]'), spotlight);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
